import {
  COST_MOVEMENT_REASON,
  COST_RECORD_KIND,
  type ProductCostHistoryResponse,
  type ProductCostMovement,
} from "@nova-ai/shared"

import { formatDateTimeInTimeZone } from "../../lib/format-date"
import { Table, type TableColumn } from "../ui/table"

type Observation = ProductCostHistoryResponse["items"][number]

const MOVEMENT_LABELS: Record<ProductCostMovement["reason"], string> = {
  [COST_MOVEMENT_REASON.first]: "No preceding observation",
  [COST_MOVEMENT_REASON.unknown]: "Amount unknown",
  [COST_MOVEMENT_REASON.placeholder]: "Placeholder amount; comparison unavailable",
  [COST_MOVEMENT_REASON.unverified]: "Currency or tax basis unverified",
  [COST_MOVEMENT_REASON.changedBasis]: "Currency or tax basis changed",
  [COST_MOVEMENT_REASON.comparable]: "Comparable monetary basis",
}

export function costTimestamp(value: string | null): string {
  return value === null ? "Unknown" : `${formatDateTimeInTimeZone(value, "UTC")} UTC`
}

function CostMovement({ observation }: { observation: Observation }) {
  const movement = observation.movement
  if (movement.reason !== COST_MOVEMENT_REASON.comparable || movement.amount === null) {
    return <span className="text-muted-foreground">{MOVEMENT_LABELS[movement.reason]}</span>
  }
  return (
    <div className="space-y-1 tabular-nums">
      <div>{`${movement.amount} ${observation.evidence.currency ?? "Unknown currency"}`}</div>

      <div className="text-xs text-muted-foreground">
        {movement.percentage === null
          ? "Percentage unavailable: zero or invalid preceding amount"
          : `${movement.percentage}%`}
      </div>
    </div>
  )
}

function CostProvenance({ observation }: { observation: Observation }) {
  return (
    <details className="max-w-sm text-xs">
      <summary className="cursor-pointer rounded text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        Source details
      </summary>

      <dl className="mt-2 space-y-2 break-words">
        <div>
          <dt>Recorded</dt>

          <dd>{costTimestamp(observation.recordedAt)}</dd>
        </div>

        <div>
          <dt>Source version</dt>

          <dd>{costTimestamp(observation.sourceVersionAt)}</dd>
        </div>

        <div>
          <dt>Mirror sync</dt>

          <dd>{costTimestamp(observation.mirrorSyncedAt)}</dd>
        </div>

        <div>
          <dt>Cost-effective date</dt>

          <dd>{costTimestamp(observation.effectiveAt)}</dd>
        </div>

        <div>
          <dt>Parent at observation</dt>

          <dd>{observation.parentSku ?? "Standalone simple"}</dd>
        </div>

        <div>
          <dt>Evidence reference</dt>

          <dd>{observation.evidence.reference ?? "None verified"}</dd>
        </div>
      </dl>
    </details>
  )
}

const COLUMNS: TableColumn<Observation>[] = [
  {
    key: "observed",
    header: "Observation (UTC)",
    render: (row) => (
      <div className="space-y-1">
        <div className="tabular-nums">{costTimestamp(row.observedAt)}</div>

        <div className="text-xs text-muted-foreground">
          {row.kind === COST_RECORD_KIND.cached ? "Cached starting value" : "Source observation"}
        </div>

        <div className="break-words text-xs text-muted-foreground">{row.source}</div>

        <CostProvenance observation={row} />
      </div>
    ),
  },
  {
    key: "amount",
    header: "Amount evidence",
    render: (row) => (
      <div className="space-y-1">
        <div className="tabular-nums">{row.amount ?? "Unknown amount"}</div>

        <div className="break-all text-xs text-muted-foreground">
          Raw: {row.rawAmount === null ? "null" : JSON.stringify(row.rawAmount)}
        </div>

        <div className="text-xs text-muted-foreground">
          {row.classification === "measured"
            ? "Numeric amount"
            : `${row.classification === "placeholder" ? "Placeholder" : "Unknown"} amount`}
        </div>
      </div>
    ),
  },
  {
    key: "basis",
    header: "Monetary basis",
    render: (row) => (
      <div className="space-y-1">
        <div>{row.evidence.currency ?? "Currency unknown"}</div>

        <div className="text-xs text-muted-foreground">
          {row.evidence.taxBasis === "unknown"
            ? "Tax basis unknown"
            : `${row.evidence.taxBasis === "net" ? "Net" : "Gross"} cost`}
        </div>

        <div className="text-xs text-muted-foreground">
          {row.evidence.status === "verified" ? "Verified evidence" : "Unverified evidence"}
        </div>
      </div>
    ),
  },
  {
    key: "movement",
    header: "Change from preceding observation",
    render: (row) => <CostMovement observation={row} />,
  },
]

export function ProductCostObservations({ items }: { items: Observation[] }) {
  return (
    <Table
      columns={COLUMNS}
      rows={items}
      rowKey={(row) => row.id}
      stackOnMobile
      empty="No observations in these UTC dates. Try a wider date range."
    />
  )
}
